import { Link } from 'react-router-dom'
import { cn } from '@/utils/cn'
import { useAppTheme } from '@/design-system/island'
import { BrandMark } from '@/design-system/primitives'
import { openCookiePreferences } from '@/utils/consent'
import '@/design-system/site.css'

/*
  A footer entry is a destination, with one exception: the cookie control
  reopens the consent bar in place rather than going anywhere. It carries
  `data-cookie-preferences` so the Astro footer, which is static HTML with no
  script of its own, can use the same attribute and the same listener.
*/
type FooterLink = { label: string; to: string } | { label: string; cookiePreferences: true }
type Group = { heading: string; links: FooterLink[] }

const groups: Group[] = [
  {
    heading: 'Products',
    links: [
      { label: 'Start free', to: '/sign-up' },
      { label: 'SuperSchema Audit', to: '/audit' },
      { label: 'SuperSchema Managed', to: '/managed' },
      { label: 'Team workflow', to: '/team-workflow' },
      { label: 'Integrations', to: '/integrations' },
      { label: 'For HubSpot sites', to: '/hubspot-aeo' },
    ]
  },
  {
    heading: 'Solutions',
    links: [
      { label: 'B2B SaaS', to: '/solutions/saas' },
      { label: 'E-commerce', to: '/solutions/ecommerce' },
      { label: 'Financial services', to: '/solutions/finance' },
      { label: 'Healthcare', to: '/solutions/healthcare' },
      { label: 'Agencies', to: '/solutions/agencies' },
      { label: 'Local services', to: '/solutions/local' },
      { label: 'Enterprise', to: '/solutions/enterprise' }
    ]
  },
  {
    heading: 'Resources',
    links: [
      { label: 'Blog', to: '/resources' },
      { label: 'Agent Readiness Course', to: '/agent-readiness-course' },
      { label: 'Customer stories', to: '/customers' },
      { label: 'Methodology', to: '/methodology' },
      { label: 'SuperSchema audit', to: '/audit' },
      { label: 'Documentation', to: '/docs' },
      { label: 'Sitemap', to: '/sitemap' }
    ]
  },
  {
    heading: 'Company',
    links: [
      { label: 'About', to: '/about' },
      { label: 'Careers', to: '/careers' },
      { label: 'Contact', to: '/contact' },
      { label: 'Press', to: '/press' },
      { label: 'Partners', to: '/partners' }
    ]
  },
  {
    heading: 'Legal & support',
    links: [
      { label: 'Get support', to: '/support' },
      { label: 'Privacy', to: '/privacy' },
      { label: 'Terms', to: '/terms' },
      { label: 'Cookies', to: '/cookies' },
      { label: 'Cookie preferences', cookiePreferences: true },
      { label: 'Security', to: '/security' }
    ]
  }
]

interface GlobalFooterProps {
  /**
   * Drops the five link groups and keeps only the wordmark and copyright.
   * For conversion landing pages, where a footer full of exits works against
   * the one action on the page.
   */
  minimal?: boolean
  /**
   * A legal line for the page that needed one, rendered on the footer's own
   * ground above the copyright. Pages that name a trademark pass it; the
   * rest pass nothing, because a notice about somebody else's mark has no
   * business on a page that never mentions them.
   */
  notice?: string
}

export default function GlobalFooter({ minimal = false, notice }: GlobalFooterProps) {
  const theme = useAppTheme()
  return (
    /* A <footer>, not a DsIsland, so the contentinfo landmark survives. */
    <footer
      className={cn('ds ds-siteend', minimal && 'ds-siteend--min')}
      data-ds-theme={theme}
    >
      <div className="ds-siteend__in">
        {!minimal && (
          <div className="ds-siteend__grid">
            {groups.map((g) => (
              <div key={g.heading}>
                <h3 className="ds-siteend__head">{g.heading}</h3>
                <ul className="ds-siteend__links">
                  {g.links.map((l) => (
                    <li key={l.label}>
                      {'to' in l ? (
                        <Link to={l.to} className="ds-siteend__link">
                          {l.label}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          className="ds-siteend__link"
                          data-cookie-preferences
                          onClick={openCookiePreferences}
                        >
                          {l.label}
                        </button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        {notice && <p className="ds-siteend__notice">{notice}</p>}

        <div className="ds-siteend__base">
          <div className="ds-siteend__name">
            <BrandMark size={22} />
            <span>SuperSchema</span>
          </div>
          <div className="ds-siteend__copy">
            © {new Date().getFullYear()} Lightbulb Moment Labs. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  )
}
